import {Observable, Subscription} from "rxjs";
import {CustomerBU, Operation, OperationCustomerPlanningSetting} from "../model/Operation";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "./AuthService";
import {Injectable} from "@angular/core";
import {apiconfig} from "../app/api.config";
import {CustomerOrderQuota} from "../model/CustomerOrderQuota";
import {OperationCard} from "../model/OperationCard";
import {LogisticKit} from "../model/LogisticKit";
import {DateService} from "./DateService";
import {
  CustomerOrderHourlyQuotaTransfer,
  CustomerOrderHourlyQuotaTransferReport,
  TransferableHourQuotaReport
} from "../model/CustomerSectorHourQuotaReport";
import {ActionDone} from "../model/LogaProxy";

@Injectable()
export class OperationService {

  apiConfig: any = apiconfig;

  constructor(protected http: HttpClient, public authService: AuthService, private dateService: DateService) {
  }

  /**
   * Get minimum info from a given operation
   * @param id
   */
  loadOperation(id: number): Observable<Operation> {

    return Observable.create(obs => {

      let h = this.authService.getHttpAuth();

      this.http
        .get(this.apiConfig.API_URL + '/operation/' + id + '/light', {headers: h})
        .subscribe(response => {
            if (response) {
              obs.next(Operation.fromJson(response));
              obs.complete();
            }
          },
          (error) => {
            // TODO :
          })
      ;

    });

  }

  /**
   *
   * Find operation for this user we get only operation with a defined quota
   *
   */
  loadOperationsWithQuota(active: boolean, openForPlanningOnly: boolean): Promise<Operation[]> {

    let h = this.authService.getHttpAuth();
    let httpParams = new HttpParams();
    httpParams = httpParams.append("opActive", active === true ? "true" : "false");
    httpParams = httpParams.append("openForPlanningOnly", openForPlanningOnly === true ? "true" : "false");


    return this.http
      .get<any[]>(this.apiConfig.API_URL + '/operation/with-quotas/', {headers: h, params: httpParams})
      .toPromise()
      .then(response => {

        let operations: Operation[] = new Array();

        if (response) {
          for (let i = 0; i < response['length']; i++) {
            operations.push(Operation.fromJson(response[i]));
          }

          // copy table to sort it
          let operationsSorted = operations.slice(0);
          operationsSorted.sort(((a, b): number => {
            return (a.name > b.name) ? 1 : -1;
          }));

          return operationsSorted;
        }
      });

  }

  /**
   * load Operation Cards: mini reports for operation, to display as short menu
   */
  loadOperationCards(): Promise<OperationCard[]> {

    let h = this.authService.getHttpAuth();
    return this.http
      .get<OperationCard[]>(this.apiConfig.API_URL + '/operation/mobile-cards/', {headers: h}).toPromise().then(opCards => {

        let operationCardsSorted: OperationCard[] = [];
        opCards.forEach(c => {
          //we enhance the DTO, and compute a few stuff after loading raw data from the api
          operationCardsSorted.push(new OperationCard().setupFrom(c, this.dateService));
        });

        // -- sort by name
        operationCardsSorted.sort(((a, b): number => {

          //sort by planning closing time first
          let dA = a.planningOpenUntil == null ? 99999 : a.planningOpenUntil;
          let dB = b.planningOpenUntil == null ? 99999 : b.planningOpenUntil;
          if (dA != dB) return dB - dA;

          //sort by activity closing time first
          dA = a.earliestPlannableDate == null ? 99999 : a.earliestPlannableDate;
          dB = b.earliestPlannableDate == null ? 99999 : b.earliestPlannableDate;
          if (dA != dB) return dB - dA;


          return (a.name > b.name) ? 1 : -1;
        }));


        return operationCardsSorted;
      });
  }

  /**
   * load customer planning settings from an operation
   * @param id
   */
  loadOperationCustomerPlanningSettings(id: number): Promise<OperationCustomerPlanningSetting> {
    const h = this.authService.getHttpAuth();
    return this.http.get<OperationCustomerPlanningSetting>(this.apiConfig.API_URL + '/operation/' + id + '/customer-planification-settings', {headers: h}).toPromise().then(a => new OperationCustomerPlanningSetting().setup(a));
  }


  public findCustomerSector(operationId: number, storeId: number): Promise<number> {
    return this.http
      .get(this.apiConfig.API_URL + '/order/customer-sector-id?operation=' + operationId + '&store=' + storeId, {headers: this.authService.getHttpAuth()}).map((h: any) => h).toPromise();
  }

  /**
   *
   * @param sector
   * @param profil
   */
  public loadHourlyQuotaStatus(sector: number, profil: number): Promise<CustomerOrderQuota> {
    let h = this.authService.getHttpAuth();
    return this.http
      .get<CustomerOrderQuota>(this.apiConfig.API_URL + '/order/customer-sector/' + sector + '/profil/' + profil + '/quota', {headers: h})
      .toPromise();
  }

  public loadTransferableHoursQuotaReport(): Promise<TransferableHourQuotaReport> {
    let h = this.authService.getHttpAuth();
    return this.http
      .get<TransferableHourQuotaReport>(this.apiConfig.API_URL + '/operation/transferable-hour-quota-report', {headers: h})
      .toPromise();
  }

  public transfertQuota(req: CustomerOrderHourlyQuotaTransfer): Promise<CustomerOrderHourlyQuotaTransferReport> {
    let h = this.authService.getHttpAuth();
    return this.http
      .put<CustomerOrderHourlyQuotaTransferReport>(
        this.apiConfig.API_URL + '/operation/customerorder-hour-quota-transfert',
        req,
        {headers: h})
      .toPromise();
  }


  public submitQuotaTransfer(operationId: number, profileId: any, sectorFromId: any, sectorToId: any, transfertQty: any): Promise<ActionDone> {
    let h = this.authService.getHttpAuth();
    return this.http.put<ActionDone>(this.apiConfig.API_URL + '/operation/' + operationId + '/customersector-hour-quota-transfer', {
      profile: profileId,
      customerSectorFrom: sectorFromId,
      customerSectorTo: sectorToId,
      transferAmount: transfertQty
    }, {headers: h}).toPromise();
  }

  public submitQuotaBudgetTransfer(operationId: number, sectorFromId: any, sectorToId: any, transfertQty: any): Promise<ActionDone> {
    let h = this.authService.getHttpAuth();
    return this.http.put<ActionDone>(this.apiConfig.API_URL + '/operation/' + operationId + '/customersector-budget-quota-transfer', {
      customerSectorFrom: sectorFromId,
      customerSectorTo: sectorToId,
      transferAmount: transfertQty
    }, {headers: h}).toPromise();
  }


  public submitQuotaBudgetToDispatch(operationId: number, customerAreaId: number, additionalQuotaInSector: { sector: number, quotaQuantity: number }[]): Promise<ActionDone> {
    let h = this.authService.getHttpAuth();
    return this.http.put<ActionDone>(this.apiConfig.API_URL + '/operation/' + operationId + '/customer-area/' + customerAreaId + '/dispatch-additional-quota', {
      operation: operationId,
      customerArea: customerAreaId,
      additionalQuotaInSector: additionalQuotaInSector
    }, {headers: h}).toPromise();
  }


}



// WEBPACK FOOTER //
// ./src/services/OperationService.ts